import { useEffect, useRef } from 'react';

/**
 * Focus management utilities for modal accessibility
 */
export const focusManager = {
  /**
   * Store the current scroll position and prevent body scroll
   * @returns The stored scroll position
   */
  lockBodyScroll: (): number => {
    if (typeof window === 'undefined') return 0;

    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return scrollY;
  },

  /**
   * Restore body scroll and scroll position
   * @param scrollY The scroll position to restore
   */
  unlockBodyScroll: (scrollY: number): void => {
    if (typeof window === 'undefined') return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, scrollY);
  },

  /**
   * Get all focusable elements within a container
   * @param container The container element
   * @returns Array of focusable elements
   */
  getFocusableElements: (container: HTMLElement): HTMLElement[] => {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]'
    ].join(', ');

    return Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[];
  },

  /**
   * Set up focus trap for modal
   * @param modalElement The modal container element
   * @returns Cleanup function
   */
  setupFocusTrap: (modalElement: HTMLElement): (() => void) => {
    const focusableElements = focusManager.getFocusableElements(modalElement);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (firstElement) {
      firstElement.focus();
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modalElement.addEventListener('keydown', handleTabKey);

    return () => {
      modalElement.removeEventListener('keydown', handleTabKey);
    };
  }
};

/**
 * Accessibility utilities for modal components
 */
export const accessibilityUtils = {
  /**
   * Generate ARIA attributes for modal
   * @param modalId Modal identifier
   * @param titleId Optional title element ID
   * @param descriptionId Optional description element ID
   * @returns Object with ARIA attributes
   */
  getModalAriaAttributes: (modalId: string, titleId?: string, descriptionId?: string) => ({
    role: 'dialog' as const,
    'aria-modal': 'true' as const,
    'aria-labelledby': titleId || undefined,
    'aria-describedby': descriptionId || undefined,
    id: modalId
  }),

  /**
   * Handle escape key press to close modal
   * @param onClose Function to call when escape is pressed
   * @returns Event handler function
   */
  createEscapeKeyHandler: (onClose: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  },

  /**
   * Generate unique IDs for modal elements
   * @param modalId Base modal identifier
   * @returns Object with generated IDs
   */
  generateModalIds: (modalId: string) => ({
    modalId: `modal-${modalId}`,
    titleId: `modal-title-${modalId}`,
    descriptionId: `modal-description-${modalId}`,
    contentId: `modal-content-${modalId}`
  })
};

/**
 * Hook for complete modal state management including accessibility
 * @param modalId Modal identifier
 * @param isOpen Whether the modal is open
 * @param onClose Modal close handler
 * @param triggerElement Element that triggered the modal (for focus return)
 * @returns Object with modal utilities and state
 */
export const useModalAccessibility = (
  modalId: string,
  isOpen: boolean,
  onClose: () => void,
  triggerElement?: HTMLElement | null
) => {
  const scrollYRef = useRef<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const focusTrapCleanupRef = useRef<(() => void) | null>(null);

  const ids = accessibilityUtils.generateModalIds(modalId);

  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = focusManager.lockBodyScroll();

      if (modalRef.current) {
        focusTrapCleanupRef.current = focusManager.setupFocusTrap(modalRef.current);
      }

      const handleEscape = accessibilityUtils.createEscapeKeyHandler(onClose);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      if (focusTrapCleanupRef.current) {
        focusTrapCleanupRef.current();
        focusTrapCleanupRef.current = null;
      }

      focusManager.unlockBodyScroll(scrollYRef.current);

      if (triggerElement && document.contains(triggerElement)) {
        triggerElement.focus();
      }
    }
  }, [isOpen, onClose, triggerElement]);

  return {
    modalRef,
    ids,
    ariaAttributes: accessibilityUtils.getModalAriaAttributes(
      ids.modalId,
      ids.titleId,
      ids.descriptionId
    )
  };
};
